import {
    Box,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextareaAutosize,
    FormHelperText,
    Checkbox,
    FormControlLabel,
    FormLabel,
    Divider,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Typography,
    OutlinedInput,
    IconButton,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import {
    CreatNotificationRequest,
    UserNotification,
} from 'types/notification/notification';
import {
    useSaveNotificationMutation,
    useUpdateNotificationMutation,
} from 'store/slices/notification/notification';
import { useRuleProductListQuery } from 'store/slices/rule/rule-entities-enum';
import SuccessSnackbarEffect from 'component/success-snackbar';
import rgba from 'assets/theme/function/rgba';
import borders from 'assets/theme/base/borders';
import {
    useNotificationGroupQuery,
    useNotificationIconNameQuery,
    useNotificationTypeQuery,
} from 'store/slices/notification/notification-enum';
import { useLazyNotificationIdQuery } from 'store/slices/notification/notification-list';
import { useUserListQuery } from 'store/slices/user/user';
import { UserList } from 'types/user/user';
import FlexibleTable from 'component/flexible-table';
import { RowDataType, TableMetaData } from 'types/common/common';
import { StyledTableRow } from 'component/tabel-pagination-style';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import AddIcon from '@mui/icons-material/Add';
import { API_ENDPOINT_URL } from 'constant';
import CloseIcon from '@mui/icons-material/Close';

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'img',
        position: 1,
        title: 'Image ',
        isSortable: false,
    },
    {
        key: 'name',
        position: 2,
        title: 'User Name',
        isSortable: false,
    },

    {
        key: 'action',
        position: 3,
        title: 'Action',
        isSortable: false,
    },
];
interface Props {
    notificationId?: string;
    status?: string;
}

const NotificationDetailsParams: React.FC<Props> = (props: Props) => {
    const { notificationId, status } = props;
    const [searchInput, setSearchInput] = useState('');
    const [selectedUsers, setSelectedUsers] = useState<UserList[]>([]);
    const {
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm<CreatNotificationRequest>({
        defaultValues: {
            notificationGroup: undefined,
            notificationIconName: undefined,
            notificationType: undefined,
            bodyList: [],
            systemBody: {
                title: undefined,
                content: undefined,
                notificationMethod: undefined,
            },
            deviceBody: {
                title: undefined,
                content: undefined,
                notificationMethod: undefined,
            },
            receiverList: [],
            ruleId: undefined,
        },
    });
    const [tableMetaDataList, setTableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [dataUser, setDataUser] = useState<UserNotification>({
        userNotification: [],
    });
    const [sort, setSort] = useState<string>('nickName');
    const [tableData, setTableData] = useState<RowDataType[]>([]);
    const isSystemNotification = watch('systemBody.notificationMethod');
    const isDeviceNotification = watch('deviceBody.notificationMethod');

    const { data: ruleProductList } = useRuleProductListQuery({
        entity: 'notification',
    });
    const { data: userList, isFetching } = useUserListQuery({
        nickname: searchInput,
    });
    const { data: notificationIconName } = useNotificationIconNameQuery();
    const { data: notificationGroup } = useNotificationGroupQuery();
    const { data: notificationType } = useNotificationTypeQuery();

    const [
        saveNotification,
        {
            data: notificationIdResponse,
            error: saveError,
            isError: saveIsError,
            isSuccess: saveIsSuccess,
            isLoading: saveIsLoading,
        },
    ] = useSaveNotificationMutation();

    const [
        updateNotification,
        {
            error: updateError,
            isError: updateIsError,
            isSuccess: updateIsSuccess,
            isLoading: updateIsLoading,
        },
    ] = useUpdateNotificationMutation();

    const [notificationIdData, { data }] = useLazyNotificationIdQuery();

    const formRef = useRef<HTMLFormElement>(null);

    const onSubmit = (values: CreatNotificationRequest) => {
        const bodyList = [values.systemBody, values.deviceBody].filter(
            (
                item,
            ): item is {
                title: string;
                content: string;
                notificationMethod: string;
            } =>
                !!item &&
                item.title.trim() !== '' &&
                item.content.trim() !== '',
        );

        const receiverList =
            dataUser?.userNotification.map((user) => ({
                userId: user.id,
            })) ?? [];

        if (notificationId || notificationIdResponse?.notificationId) {
            updateNotification({
                ruleId: values.ruleId,
                notificationGroup: values.notificationGroup,
                notificationIconName: values.notificationIconName,
                notificationType: values.notificationType,
                notificationId:
                    notificationId ?? notificationIdResponse?.notificationId,
                receiverList,
                bodyList,
            });
        } else {
            saveNotification({
                ruleId: values.ruleId,
                notificationGroup: values.notificationGroup,
                notificationIconName: values.notificationIconName,
                notificationType: values.notificationType,
                receiverList,
                bodyList,
            });
        }
    };

    const addUserData = () => {
        const usersData = selectedUsers.map((u) => ({
            id: u.id,
            name: u.name,
        }));

        setDataUser((prev) => {
            const existingIds = new Set(
                prev.userNotification?.map((u) => u.id) ?? [],
            );
            const newUsers = usersData.filter((u) => !existingIds.has(u.id));

            return {
                ...prev,
                userNotification: [
                    ...(prev.userNotification ?? []),
                    ...newUsers,
                ],
            };
        });

        setSelectedUsers([]);
    };

    const deleteUser = (id: string) => {
        setDataUser((prev) => {
            if (!prev.userNotification) return prev;
            return {
                ...prev,
                userNotification: prev.userNotification.filter(
                    (user) => user.id !== id,
                ),
            };
        });
    };

    const dispatch = useAppDispatch();

    useEffect(() => {
        const tableData: RowDataType[] =
            dataUser.userNotification?.map((fullInfo) => {
                const user = userList?.find((u) => u.id === fullInfo.id);
                return {
                    ...fullInfo,
                    img: user?.selectAvatarId ? (
                        <Box
                            component="img"
                            src={`${API_ENDPOINT_URL}/avatar/file/${user.selectAvatarId}`}
                            alt=""
                            sx={{
                                width: 60,
                                height: 60,
                                objectFit: 'cover',
                                objectPosition: 'top',
                                borderRadius: borders.borderRadius.section,
                            }}
                        />
                    ) : (
                        <Typography variant="h5">-</Typography>
                    ),
                    action: (
                        <Box display="flex" justifyContent="center" gap={2}>
                            <Tooltip title="Delete">
                                <Delete
                                    cursor="pointer"
                                    onClick={() => deleteUser(fullInfo.id)}
                                />
                            </Tooltip>
                        </Box>
                    ),
                    rowComponent: StyledTableRow,
                };
            }) || [];

        setTableData(tableData);
    }, [dataUser.userNotification, userList]);

    useEffect(() => {
        if (!data || !userList) return;

        const users = userList.filter((u) =>
            data.notificationReceiverList?.some((r) => r.userId === u.id),
        );

        const systemBody = data.notificationBodyList.find(
            (b) => b.notificationMethod === 'system-notification',
        );

        const deviceBody = data.notificationBodyList.find(
            (b) => b.notificationMethod === 'device-notification',
        );

        reset({
            notificationGroup: data.notificationGroup ?? '',
            notificationIconName: data.iconName ?? '',
            notificationType: data.notificationType ?? '',
            ruleId: data.ruleId ?? '',
            systemBody: systemBody ?? {
                title: '',
                content: '',
                notificationMethod: undefined,
            },
            deviceBody: deviceBody ?? {
                title: '',
                content: '',
                notificationMethod: undefined,
            },
            userNotification: users.map((u) => u.id) ?? [],
        });

        setDataUser({ userNotification: users });
    }, [data, userList, reset]);

    useEffect(() => {
        if (
            isSystemNotification === undefined &&
            isDeviceNotification === undefined
        )
            return;

        if (!isDeviceNotification) {
            setValue('deviceBody.title', '');
            setValue('deviceBody.content', '');
        }
        if (!isSystemNotification) {
            setValue('systemBody.title', '');
            setValue('systemBody.content', '');
        }
    }, [isDeviceNotification, isSystemNotification, setValue]);

    useEffect(() => {
        const id = notificationId ?? notificationIdResponse?.notificationId;

        if (id) {
            notificationIdData(id);
        }
    }, [
        notificationId,
        notificationIdResponse?.notificationId,
        saveIsSuccess,
        updateIsSuccess,
    ]);

    useEffect(() => {
        const error = saveError || updateError;
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [saveIsError, updateIsError]);

    return (
        <>
            <Box
                border={`1px solid ${rgba(colors.info.main, 0.5)}`}
                borderRadius={borders.borderRadius.md}
                p={pxToRem(10)}
                ref={formRef}
                component="form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <Box display={'flex'}>
                    <FormControl fullWidth>
                        <InputLabel>Icon Name</InputLabel>
                        <Controller
                            name="notificationIconName"
                            control={control}
                            rules={{ required: 'Icon Name is required' }}
                            render={({
                                field: { name, value = '', onChange },
                            }) => (
                                <Select
                                    label="Icon Name"
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    input={
                                        <OutlinedInput
                                            label="Icon Name"
                                            endAdornment={
                                                value && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            onChange('')
                                                        }
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            top: '50%',
                                                            right: 25,
                                                            transform:
                                                                'translateY(-50%)',
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            }
                                        />
                                    }
                                >
                                    {notificationIconName?.map(
                                        (status, index) => (
                                            <MenuItem
                                                key={index}
                                                value={status}
                                            >
                                                {status}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            )}
                        />
                        {errors.notificationIconName && (
                            <FormHelperText>
                                {errors.notificationIconName.message}
                            </FormHelperText>
                        )}
                    </FormControl>
                    <FormControl fullWidth sx={{ ml: pxToRem(10) }}>
                        <InputLabel>Notification Group</InputLabel>
                        <Controller
                            name="notificationGroup"
                            control={control}
                            rules={{
                                required: 'Notification Group is required',
                            }}
                            render={({
                                field: { name, value = '', onChange },
                            }) => (
                                <Select
                                    label="Notification Group"
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    input={
                                        <OutlinedInput
                                            label="Notification Group"
                                            endAdornment={
                                                value && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            onChange('')
                                                        }
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            top: '50%',
                                                            right: 25,
                                                            transform:
                                                                'translateY(-50%)',
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            }
                                        />
                                    }
                                >
                                    {notificationGroup?.map((status, index) => (
                                        <MenuItem key={index} value={status}>
                                            {status}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                        {errors.notificationGroup && (
                            <FormHelperText>
                                {errors.notificationGroup.message}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl fullWidth sx={{ ml: pxToRem(10) }}>
                        <InputLabel>Type</InputLabel>
                        <Controller
                            name="notificationType"
                            control={control}
                            rules={{
                                required: 'Notification Type is required',
                            }}
                            render={({
                                field: { name, value = '', onChange },
                            }) => (
                                <Select
                                    label="Type"
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    input={
                                        <OutlinedInput
                                            label="Type"
                                            endAdornment={
                                                value && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            onChange('')
                                                        }
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            top: '50%',
                                                            right: 25,
                                                            transform:
                                                                'translateY(-50%)',
                                                            zIndex: 1,
                                                        }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                )
                                            }
                                        />
                                    }
                                >
                                    {notificationType?.map((status, index) => (
                                        <MenuItem key={index} value={status}>
                                            {status}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                        {errors.notificationType && (
                            <FormHelperText>
                                {errors.notificationType.message}
                            </FormHelperText>
                        )}
                    </FormControl>
                    <FormControl fullWidth sx={{ ml: pxToRem(10) }}>
                        <InputLabel>Rule</InputLabel>
                        <Controller
                            name="ruleId"
                            control={control}
                            render={({ field: { name, value, onChange } }) => {
                                return (
                                    <Select
                                        label="Rule"
                                        name={name}
                                        value={value ?? ''}
                                        onChange={(e) => {
                                            onChange(e.target.value);
                                        }}
                                        input={
                                            <OutlinedInput
                                                label="Rule"
                                                endAdornment={
                                                    value && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                onChange('')
                                                            }
                                                            sx={{
                                                                position:
                                                                    'absolute',
                                                                top: '50%',
                                                                right: 25,
                                                                transform:
                                                                    'translateY(-50%)',
                                                                zIndex: 1,
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    )
                                                }
                                            />
                                        }
                                    >
                                        {ruleProductList?.map((category) => (
                                            <MenuItem
                                                key={category.id.toString()}
                                                value={category.id.toString()}
                                            >
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                );
                            }}
                        />
                    </FormControl>
                </Box>
                <Divider sx={{ mt: pxToRem(20), mb: pxToRem(10) }} />
                <Box display={'flex'} alignItems={'center'}>
                    <FormControlLabel
                        label="System"
                        control={
                            <Controller
                                name="systemBody.notificationMethod"
                                control={control}
                                render={({ field }) => (
                                    <Checkbox
                                        checked={
                                            field.value ===
                                            'system-notification'
                                        }
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            field.onChange(
                                                checked
                                                    ? 'system-notification'
                                                    : undefined,
                                            );
                                        }}
                                    />
                                )}
                            />
                        }
                        sx={{
                            '.MuiFormControlLabel-label': {
                                marginLeft: pxToRem(10),
                            },
                            ml: pxToRem(15),
                        }}
                    />
                    <Box flex={1} display="flex" justifyContent="center">
                        <FormControlLabel
                            label="Device"
                            control={
                                <Controller
                                    name="deviceBody.notificationMethod"
                                    control={control}
                                    render={({ field }) => (
                                        <Checkbox
                                            sx={{ ml: pxToRem(10) }}
                                            checked={
                                                field.value ===
                                                'device-notification'
                                            }
                                            onChange={(e) => {
                                                const checked =
                                                    e.target.checked;
                                                field.onChange(
                                                    checked
                                                        ? 'device-notification'
                                                        : undefined,
                                                );
                                            }}
                                        />
                                    )}
                                />
                            }
                            sx={{
                                '.MuiFormControlLabel-label': {
                                    marginLeft: pxToRem(10),
                                },
                            }}
                        />
                    </Box>
                </Box>
                <Box display={'flex'} mt={pxToRem(10)}>
                    <Box
                        display={'grid'}
                        width={'100%'}
                        border={`1px solid ${rgba(colors.info.main, 1)}`}
                        borderRadius={borders.borderRadius.md}
                        p={pxToRem(10)}
                    >
                        <Controller
                            name="systemBody.title"
                            control={control}
                            rules={{
                                required: !isSystemNotification
                                    ? false
                                    : 'Title is required',
                            }}
                            render={({
                                field: { name, value = '', onChange },
                            }) => (
                                <TextField
                                    disabled={!isSystemNotification}
                                    fullWidth
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    label="Notification System Title"
                                />
                            )}
                        />

                        <FormControl
                            fullWidth
                            sx={{ pt: pxToRem(10) }}
                            disabled={!isSystemNotification}
                        >
                            <FormLabel>System Notification Context</FormLabel>
                            <Controller
                                name="systemBody.content"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <>
                                        <TextareaAutosize
                                            disabled={!isSystemNotification}
                                            {...field}
                                            minRows={3}
                                            style={{
                                                backgroundColor: 'transparent',
                                                color: colors.common.white,
                                                width: '100%',
                                                fontSize: pxToRem(14),
                                                paddingLeft: pxToRem(8),
                                                paddingTop: pxToRem(4),
                                                borderColor:
                                                    !isSystemNotification
                                                        ? colors.divider
                                                        : colors.common.white,
                                                borderRadius: 4,
                                            }}
                                        />
                                        {error && (
                                            <FormHelperText error>
                                                {error.message}
                                            </FormHelperText>
                                        )}
                                    </>
                                )}
                            />
                        </FormControl>
                    </Box>
                    <Box
                        display={'grid'}
                        width={'100%'}
                        border={`1px solid ${rgba(colors.info.main, 1)}`}
                        borderRadius={borders.borderRadius.md}
                        p={pxToRem(10)}
                        ml={pxToRem(10)}
                    >
                        <Controller
                            name="deviceBody.title"
                            control={control}
                            rules={{
                                required: !isDeviceNotification
                                    ? false
                                    : 'Title is required',
                            }}
                            render={({
                                field: { name, value = '', onChange },
                            }) => (
                                <TextField
                                    disabled={!isDeviceNotification}
                                    fullWidth
                                    name={name}
                                    value={value}
                                    onChange={onChange}
                                    label="Notification Device Title"
                                />
                            )}
                        />
                        <FormControl
                            fullWidth
                            sx={{ pt: pxToRem(10) }}
                            disabled={!isDeviceNotification}
                        >
                            <FormLabel>Device Notification Context</FormLabel>
                            <Controller
                                name="deviceBody.content"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <>
                                        <TextareaAutosize
                                            disabled={!isDeviceNotification}
                                            {...field}
                                            minRows={3}
                                            style={{
                                                backgroundColor: 'transparent',
                                                color: colors.common.white,
                                                width: '100%',
                                                fontSize: pxToRem(14),
                                                paddingLeft: pxToRem(8),
                                                paddingTop: pxToRem(4),
                                                borderColor:
                                                    !isDeviceNotification
                                                        ? colors.divider
                                                        : colors.common.white,
                                                borderRadius: 4,
                                            }}
                                        />
                                        {error && (
                                            <FormHelperText error>
                                                {error.message}
                                            </FormHelperText>
                                        )}
                                    </>
                                )}
                            />
                        </FormControl>
                    </Box>
                </Box>
                <Box display={'flex'} pt={pxToRem(10)} alignItems={'center'}>
                    <Controller
                        name="userNotification"
                        control={control}
                        render={({ field: { onChange, ref } }) => (
                            <Autocomplete
                                limitTags={5}
                                disableCloseOnSelect
                                multiple
                                sx={{ width: '50%' }}
                                options={userList ?? []}
                                loading={isFetching}
                                value={selectedUsers}
                                onChange={(_, newValue) => {
                                    setSelectedUsers(newValue);
                                    onChange(newValue.map((u) => u.id));
                                }}
                                inputValue={searchInput}
                                onInputChange={(_, newInput, reason) => {
                                    if (reason !== 'reset') {
                                        setSearchInput(newInput);
                                    }
                                }}
                                getOptionLabel={(option) => option.name || ''}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                filterSelectedOptions
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        label="Users"
                                        variant="outlined"
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {isFetching && (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={20}
                                                        />
                                                    )}
                                                    {
                                                        params.InputProps
                                                            .endAdornment
                                                    }
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}
                    />
                    <Box sx={{ cursor: 'pointer' }} onClick={addUserData}>
                        <AddIcon fontSize={'large'} />
                    </Box>
                </Box>
            </Box>
            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(15)}
            >
                <FlexibleTable
                    tableMetaDataList={tableMetaDataList}
                    setTableMetaDataList={setTableMetaDataList}
                    tableDataList={tableData}
                    sort={sort}
                    setSort={setSort}
                />
            </Box>
            {status !== 'active' && (
                <Box textAlign={'end'} pt={pxToRem(10)}>
                    <Button
                        sx={{ width: '10%' }}
                        disabled={
                            !isDeviceNotification && !isSystemNotification
                        }
                        onClick={() => formRef.current?.requestSubmit()}
                        variant={'contained'}
                        loading={saveIsLoading || updateIsLoading}
                    >
                        {notificationId || notificationIdResponse
                            ? 'Update'
                            : 'Save'}
                    </Button>
                </Box>
            )}
            <SuccessSnackbarEffect
                isSuccess={saveIsSuccess || updateIsSuccess}
                message={'Successfully Created an Notification!'}
            />
        </>
    );
};
export default NotificationDetailsParams;
